import type { AppRouter } from '@gallerywall/shared';
import { createTRPCReact, httpBatchLink, type TRPCLink } from '@trpc/react-query';
import { observable } from '@trpc/server/observable';
import { clearToken, getValidToken, hasRefreshToken, refreshSession, signInUrl } from './auth';

/**
 * Typed tRPC client bound to the gallerywall AppRouter. Gives full IntelliSense
 * + compile-time validation on every call from React components.
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * URL of the tRPC server. Set at build time via Vite env var.
 *
 * Production builds (CI for Phase 0.4):
 *   VITE_TRPC_URL=https://<lambda-id>.lambda-url.us-east-1.on.aws
 *
 * Local dev (Phase 0.3d):
 *   VITE_TRPC_URL=http://localhost:3000   (via .env.local)
 *
 * Fallback is `/trpc` (relative) — useful if we ever route the API
 * through CloudFront instead of direct Function URL.
 */
const apiUrl = import.meta.env.VITE_TRPC_URL ?? '/trpc';

/**
 * Reactive half of session refresh (Phase 1a-UX-polish; the proactive half is
 * getValidToken in the headers function below). If a call comes back
 * UNAUTHORIZED while we hold a refresh token — proactive refresh can't catch
 * everything (clock skew, token revoked server-side) — refresh once and replay
 * the call. If the refresh itself fails, the session is over: clear tokens and
 * hand the user to Cognito sign-in.
 */
const authRetryLink: TRPCLink<AppRouter> = () => {
  return ({ next, op }) =>
    observable((observer) => {
      let current = next(op).subscribe({
        next: (v) => observer.next(v),
        complete: () => observer.complete(),
        error: (err) => {
          if (err.data?.code !== 'UNAUTHORIZED' || !hasRefreshToken()) {
            observer.error(err);
            return;
          }
          refreshSession().then((ok) => {
            if (!ok) {
              clearToken();
              window.location.href = signInUrl();
              observer.error(err);
              return;
            }
            // Replay once with the fresh token. A second UNAUTHORIZED is
            // terminal — don't loop.
            current = next(op).subscribe({
              next: (v) => observer.next(v),
              complete: () => observer.complete(),
              error: (err2) => {
                if (err2.data?.code === 'UNAUTHORIZED') {
                  clearToken();
                  window.location.href = signInUrl();
                }
                observer.error(err2);
              },
            });
          });
        },
      });
      return () => current.unsubscribe();
    });
};

export const trpcClient = trpc.createClient({
  links: [
    authRetryLink,
    httpBatchLink({
      url: apiUrl,
      // Runs per-batch; getValidToken reads the current id_token fresh from
      // localStorage and silently refreshes it when it's within 5 minutes of
      // expiry (debounced — parallel batches share one refresh). Returns no
      // Authorization when unauthenticated; the API then sees ctx.user = null
      // and `protectedProcedure` returns UNAUTHORIZED.
      headers: async () => {
        const token = await getValidToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
    }),
  ],
});
